import { mergeAttributes, Node } from "@tiptap/core";
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditorState, type ReactNodeViewProps } from "@tiptap/react";
import type { Node as PMNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { useEffect, useState } from "react";
import { useRowById } from "../../db/rows";
import { Popover } from "../Menu";
import { dateFmt } from "../format";

/*
 * Evernote's newer Insert blocks: Callout, Page break, Table of contents, Mention a date.
 * (Toggle is TipTap's Details extension.) Each stores plain, readable HTML.
 */

export const CALLOUT_EMOJIS = ["💡", "📌", "⚠️", "❗", "✅", "❓", "📝", "🔥", "⭐", "💬", "📅", "🚀"] as const;
export const CALLOUT_COLORS = ["yellow", "blue", "green", "red", "purple", "gray"] as const;

/** A tinted box with an emoji, holding any blocks. */
export const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      emoji: { default: "💡", parseHTML: (el) => el.getAttribute("data-emoji") || "💡", renderHTML: (a) => ({ "data-emoji": a.emoji }) },
      color: {
        default: "yellow",
        parseHTML: (el) => ((CALLOUT_COLORS as readonly string[]).includes(el.getAttribute("data-color") ?? "") ? el.getAttribute("data-color") : "yellow"),
        renderHTML: (a) => ({ "data-color": a.color }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-callout]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes({ "data-callout": "" }, HTMLAttributes), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(CalloutView);
  },
});

function CalloutView({ node, updateAttributes, editor }: ReactNodeViewProps) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  return (
    <NodeViewWrapper className="callout" data-color={node.attrs.color}>
      <button
        className="callout-emoji"
        contentEditable={false}
        disabled={!editor.isEditable}
        title="Change icon and color"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => setMenu(menu ? null : e.currentTarget)}
      >
        {node.attrs.emoji}
      </button>
      <NodeViewContent className="callout-body" />
      {menu && (
        <Popover anchor={menu} onClose={() => setMenu(null)} className="menu callout-menu">
          <div className="menu-heading">Icon</div>
          <div className="callout-emojis">
            {CALLOUT_EMOJIS.map((em) => (
              <button key={em} className={`callout-pick${node.attrs.emoji === em ? " on" : ""}`} onClick={() => updateAttributes({ emoji: em })}>
                {em}
              </button>
            ))}
          </div>
          <div className="menu-heading">Color</div>
          <div className="callout-colors">
            {CALLOUT_COLORS.map((c) => (
              <button key={c} className={`callout-swatch${node.attrs.color === c ? " on" : ""}`} data-color={c} title={c} onClick={() => updateAttributes({ color: c })} />
            ))}
          </div>
        </Popover>
      )}
    </NodeViewWrapper>
  );
}

/** Starts a new page when printed; a labelled dashed line on screen. */
export const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML() {
    return [{ tag: "div[data-page-break]" }];
  },
  renderHTML() {
    return ["div", { "data-page-break": "", style: "break-after: page" }];
  },
});

/** Lists the note's headings; click one to jump to it. Rebuilt from the note, so it never goes stale. */
export const TableOfContents = Node.create({
  name: "toc",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML() {
    return [{ tag: "div[data-toc]" }];
  },
  renderHTML() {
    return ["div", { "data-toc": "" }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TocView);
  },
});

type Heading = { level: number; text: string; pos: number };
const NONE: Heading[] = [];
/*
 * The headings inside a node, positions relative to its content, cached per node. Typing makes a new node only along
 * the path to the caret (ProseMirror shares the rest), so after a keystroke just that path is walked again: a note
 * with a 2,080-cell table re-walked every cell on every key before.
 */
const headingCache = new WeakMap<PMNode, Heading[]>();
function headingsIn(node: PMNode): Heading[] {
  const hit = headingCache.get(node);
  if (hit) return hit;
  let out = NONE;
  if (!node.isTextblock) {
    node.forEach((child, offset) => {
      const base = offset + 1; // the child's content starts after its opening token
      if (child.type.name === "heading") out = [...out, { level: child.attrs.level as number, text: child.textContent, pos: offset }];
      else {
        const inner = headingsIn(child);
        if (inner.length) out = [...out, ...inner.map((h) => ({ ...h, pos: h.pos + base }))];
      }
    });
  }
  headingCache.set(node, out);
  return out;
}
const sameHeadings = (a: Heading[], b: Heading[]) =>
  a.length === b.length && a.every((h, i) => h.level === b[i]!.level && h.text === b[i]!.text && h.pos === b[i]!.pos);

function TocView({ editor, selected }: ReactNodeViewProps) {
  const headings = useEditorState({ editor, selector: ({ editor: e }) => headingsIn(e.state.doc), equalityFn: (a, b) => !!b && sameHeadings(a, b) });
  const go = (pos: number) => {
    const { view } = editor;
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos + 1)).scrollIntoView());
    view.focus();
    (view.nodeDOM(pos) as HTMLElement | null)?.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  return (
    <NodeViewWrapper className={`toc${selected ? " selected" : ""}`} contentEditable={false}>
      <div className="toc-title">Table of contents</div>
      {headings?.length ? (
        <ul>
          {headings.map((h) => (
            <li key={h.pos} className={`toc-l${h.level}`}>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => go(h.pos)}>
                {h.text || "Untitled heading"}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="toc-empty">Add headings to build the table of contents.</div>
      )}
    </NodeViewWrapper>
  );
}

/**
 * Evernote's inline tag: a "#tag" chip in the text. Inserting one also tags the note (so tag search and the
 * tag list find it); the chip shows the tag's current name, so renaming the tag updates it.
 */
export const InlineTag = Node.create({
  name: "inlineTag",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      id: { default: null, parseHTML: (el) => el.getAttribute("data-inline-tag"), renderHTML: (a) => ({ "data-inline-tag": a.id }) },
      name: { default: "", parseHTML: (el) => (el.textContent ?? "").replace(/^#/, ""), renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-inline-tag]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return ["span", mergeAttributes({ class: "inline-tag" }, HTMLAttributes), `#${node.attrs.name}`];
  },
  renderText({ node }) {
    return `#${node.attrs.name}`;
  },
  addNodeView() {
    return ReactNodeViewRenderer(InlineTagView, { as: "span" });
  },
});

function InlineTagView({ node, selected }: ReactNodeViewProps) {
  const row = useRowById<{ name: string; deleted: number }>("tags", node.attrs.id);
  const tag = row && !row.deleted ? row : null;
  return (
    <NodeViewWrapper as="span" className={`inline-tag${selected ? " selected" : ""}`} data-inline-tag={node.attrs.id}>
      #{tag?.name ?? node.attrs.name}
    </NodeViewWrapper>
  );
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayIso = () => iso(new Date());
/** "Oct 5, 2026" for a YYYY-MM-DD date, read in local time. */
export function mentionLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return dateFmt({ year: "numeric", month: "short", day: "numeric" }).format(new Date(y, m - 1, d));
}

/** An inline date chip (Evernote's "Mention a date"); click it to pick another date. */
export const DateMention = Node.create({
  name: "dateMention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      date: {
        default: null,
        parseHTML: (el) => (/^\d{4}-\d{2}-\d{2}$/.test(el.getAttribute("datetime") ?? "") ? el.getAttribute("datetime") : null),
        renderHTML: (a) => ({ datetime: a.date }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "time[data-date-mention]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return ["time", mergeAttributes({ "data-date-mention": "" }, HTMLAttributes), node.attrs.date ? mentionLabel(node.attrs.date) : ""];
  },
  renderText({ node }) {
    return node.attrs.date ? mentionLabel(node.attrs.date) : "";
  },
  addNodeView() {
    return ReactNodeViewRenderer(DateView, { as: "span" });
  },
});

function DateView({ node, updateAttributes, selected, editor }: ReactNodeViewProps) {
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!selected) setClosed(false);
  }, [selected]);
  // Evernote opens the date picker right after you insert it, and when you pick the chip again.
  const open = selected && editor.isEditable && !closed;
  return (
    <NodeViewWrapper as="span" className={`date-mention${selected ? " selected" : ""}`}>
      <span ref={setAnchor} onClick={() => setClosed(false)}>
        {node.attrs.date ? mentionLabel(node.attrs.date) : "Pick a date"}
      </span>
      {open && anchor && (
        <Popover anchor={anchor} onClose={() => setClosed(true)} className="menu date-menu">
          <input
            type="date"
            className="date-input"
            autoFocus
            defaultValue={node.attrs.date ?? todayIso()}
            onChange={(e) => e.target.value && updateAttributes({ date: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") {
                e.preventDefault();
                setClosed(true);
                editor.commands.focus();
              }
            }}
          />
        </Popover>
      )}
    </NodeViewWrapper>
  );
}
